let idleTimer = null;

const idleTimeLimit = 20 * 60 * 1000;
const lastActivityKey = 'sasai-last-activity';

const activityEvents = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];

let handlersAttached = false;
let routerRef = null;
let timeoutCallback = null;
let closing = false;
let lastWrite = 0;

function getLastActivity() {
  const value = Number(localStorage.getItem(lastActivityKey));

  return Number.isFinite(value) && value > 0 ? value : 0;
}

export function isIdleExpired() {
  const lastActivity = getLastActivity();

  return lastActivity > 0 && Date.now() - lastActivity >= idleTimeLimit;
}

// Ejecutar solamente después de un login exitoso.
export function beginIdleSession() {
  localStorage.setItem(lastActivityKey, String(Date.now()));
}

function scheduleCheck() {
  clearTimeout(idleTimer);

  if (!handlersAttached || closing) {
    return;
  }

  const remaining = idleTimeLimit - (Date.now() - getLastActivity());

  if (remaining <= 0) {
    expireSession();
    return;
  }

  idleTimer = setTimeout(checkExpiration, Math.min(remaining, 15000));
}

function checkExpiration() {
  if (!handlersAttached || closing) {
    return;
  }

  if (isIdleExpired()) {
    expireSession();
    return;
  }

  scheduleCheck();
}

function onActivity(event) {
  if (!handlersAttached || closing || !event.isTrusted) {
    return;
  }

  // Primero verifica el vencimiento.
  // Una sesión vencida no se reactiva con un clic.
  if (isIdleExpired()) {
    expireSession();
    return;
  }

  const now = Date.now();

  if (now - lastWrite >= 1000) {
    localStorage.setItem(lastActivityKey, String(now));
    lastWrite = now;
  }

  scheduleCheck();
}

function onVisibilityChange() {
  if (document.visibilityState === 'visible') {
    checkExpiration();
  }
}

function onStorage(event) {
  if (event.key !== lastActivityKey) {
    return;
  }

  if (!event.newValue) {
    // Otra pestaña cerró la sesión.
    expireSession();
    return;
  }

  checkExpiration();
}

function expireSession() {
  if (closing) {
    return;
  }

  closing = true;

  const callback = timeoutCallback;
  const router = routerRef;

  stopIdleWatcher();

  // Limpieza inmediata, sin depender de la conexión.
  localStorage.removeItem('jhi-authenticationToken');
  sessionStorage.removeItem('jhi-authenticationToken');
  localStorage.removeItem(lastActivityKey);
  sessionStorage.removeItem('requested-url');

  // No esperamos al servidor para redirigir.
  try {
    if (typeof callback === 'function') {
      Promise.resolve(callback()).catch(error => {
        console.error('Error al cerrar sesión:', error);
      });
    }
  } catch (error) {
    console.error('Error al cerrar sesión:', error);
  }

  // Navega dentro de la aplicación, incluso sin conexión.
  if (router && router.currentRoute.name !== 'Home') {
    router.replace({ name: 'Home' }).catch(error => {
      if (error.name !== 'NavigationDuplicated') {
        console.error('Error al regresar al inicio:', error);
      }
    });
  }
}

export function startIdleWatcher(router, onTimeout) {
  routerRef = router;
  timeoutCallback = onTimeout;

  if (handlersAttached) {
    checkExpiration();
    return;
  }

  closing = false;
  lastWrite = 0;

  // Conserva la fecha anterior al recargar.
  if (!getLastActivity()) {
    beginIdleSession();
  }

  activityEvents.forEach(event => {
    window.addEventListener(event, onActivity, {
      passive: true,
    });
  });

  document.addEventListener('visibilitychange', onVisibilityChange);

  window.addEventListener('focus', checkExpiration);
  window.addEventListener('pageshow', checkExpiration);
  window.addEventListener('online', checkExpiration);
  window.addEventListener('storage', onStorage);

  handlersAttached = true;

  checkExpiration();
}

export function stopIdleWatcher() {
  clearTimeout(idleTimer);
  idleTimer = null;

  activityEvents.forEach(event => {
    window.removeEventListener(event, onActivity);
  });

  document.removeEventListener('visibilitychange', onVisibilityChange);

  window.removeEventListener('focus', checkExpiration);
  window.removeEventListener('pageshow', checkExpiration);
  window.removeEventListener('online', checkExpiration);
  window.removeEventListener('storage', onStorage);

  handlersAttached = false;
}

export default {
  startIdleWatcher,
  stopIdleWatcher,
  beginIdleSession,
  isIdleExpired,
};
